// Copyright (c) Bentley Systems
import { ProgressRadial, Text } from "@itwin/itwinui-react";
import { AreaModel } from "./Models/area-model";
import { useEffect, useMemo, useState } from "react";
import { selectAccessToken } from "../app/authorization-slice";
import {
  getUrlBasedOnEnv,
  setErrorState,
} from "../Helpers/helper-functions";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  selectHeader,
  selectHeaders,
  setActiveAreaId,
  setActiveNodeId,
  setHeader,
  setHeaders,
  setHideSideMenu,
} from "../app/header-slice";
import { FluidGrid } from "@itwin/itwinui-layouts-react";
import BecsTile from "./BecsTile";

const AreasList = () => {
  const dispatch = useAppDispatch();
  const [areas, setAreas] = useState<AreaModel[]>([]);
  const [appHeader, setAppHeader] = useState<string | undefined>();
  const [isLoading, setLoading] = useState<boolean>(true);
  const accessToken = useAppSelector(selectAccessToken);
  const appAreas = useAppSelector(selectHeaders);
  const header = useAppSelector(selectHeader);

  useEffect(() => {
    // setAreas(appAreas);
    dispatch(setActiveAreaId(undefined));
    dispatch(setActiveNodeId(undefined));
    dispatch(setHideSideMenu(false));
    if (appAreas.length > 0) {
      setAreas(appAreas);
      setAppHeader(header);
      setLoading(false);
    } else {
      const abortController = new AbortController();
      const url = getUrlBasedOnEnv("/api/Structure/GetAppAreas");
      const runId = crypto.randomUUID();
      fetch(url, {
        method: "GET",
        headers: {
          Authorization: accessToken,
          RunId: runId,
        },
        signal: abortController.signal,
      })
        .then(async function (response) {
          if (!response.ok) {
            setErrorState(response.status, dispatch, runId);
            return;
          }
          return response.json();
        })
        .then(function (data) {
          if (!data) {
            return;
          }
          setAreas(data.areas);
          if (data.header) {
            setAppHeader(data.header);
          }
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            setErrorState(500, dispatch, runId, true);
          }
        })
        .finally(() => {
          setLoading(false);
        });

      // Cleanup function
      return () => {
        abortController.abort();
      };
    }
  }, [accessToken, dispatch]);

  const items = useMemo(() => {
    function setHeadersMenuWithAreas() {
      const areasModels = [...areas].sort(
        (a, b) => (a.areaOrder as number) - (b.areaOrder as number),
      );
      dispatch(setHeaders(areasModels));
      dispatch(setHeader(appHeader));
    }

    return [...areas]
      .sort((a, b) => (a.areaOrder as number) - (b.areaOrder as number))
      .map((area) => (
        <BecsTile
          key={area.applAreaId}
          area={area}
          setHeaderMenuWithAreas={setHeadersMenuWithAreas}
        />
      ));
  }, [areas, dispatch]);

  return (
    <div className="becs-header-logo-margin-align">
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <ProgressRadial indeterminate />
        </div>
      ) : items.length === 0 ? (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div className="iui-table-empty">
            <div>No data.</div>
          </div>
        </div>
      ) : (
        <div>
          {appHeader && (
            <Text
              variant="title"
              as="h1"
              className="becs-header-logo-margin-bottom"
            >
              {appHeader}
            </Text>
          )}
          <FluidGrid>{items}</FluidGrid>
        </div>
      )}
    </div>
  );
};

export default AreasList;
